import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';

const SERVICE_COLORS = { 'Compute Engine':'#2563eb','Cloud Storage':'#16a34a','Kubernetes Engine':'#7c3aed','Cloud Logging':'#d97706','Networking':'#0891b2','Cloud Monitoring':'#dc2626','Artifact Registry':'#f59e0b','Vertex AI':'#6366f1' };
const COLORS = ['#2563eb','#16a34a','#d97706','#7c3aed','#0891b2','#dc2626','#f59e0b','#6366f1'];

const downloadFile = async (format, token) => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const resp = await fetch(`${base}/api/resources/download-report?format=${format}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error('Download failed');
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `gcp-billing-report.${format}`;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

export default function ServicesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get('/resources/services-detail')
      .then(({ data: res }) => setData(res))
      .catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  const handleDownload = async (format) => {
    setDownloading(format);
    try { await downloadFile(format, token); }
    catch (e) { alert('Download failed: ' + e.message); }
    finally { setDownloading(null); }
  };

  const filtered = (data?.services || []).filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase())
  );

  const serviceBarData = (data?.services || []).map(s => ({
    name: s.name.replace('Cloud ', '').replace(' Engine', ''),
    skus: s.skuCount,
    color: SERVICE_COLORS[s.name] || '#94a3b8',
  }));

  const projectPieData = [
    { name: 'Billing Enabled', value: data?.totalProjectsBilled || 0, color: '#16a34a' },
    { name: 'Billing Disabled', value: (data?.projects?.length || 0) - (data?.totalProjectsBilled || 0), color: '#e2e8f0' },
  ].filter(d => d.value > 0);

  return (
    <div style={s.layout}>
      <Sidebar />
      <main style={s.main}>

        <div style={s.header}>
          <div>
            <div style={s.breadcrumb}>Billing</div>
            <h1 style={s.title}>Service Breakdown</h1>
            <p style={s.subtitle}>GCP services on your billing account · SKU catalog from Cloud Billing API</p>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            {data?.fetchedAt && <span style={s.refreshTime}>Updated {new Date(data.fetchedAt).toLocaleTimeString()}</span>}
            <button style={s.outlineBtn} onClick={() => handleDownload('csv')} disabled={!!downloading || !data}>
              {downloading === 'csv' ? '⏳' : '⬇'} CSV
            </button>
            <button style={s.outlineBtn} onClick={() => handleDownload('json')} disabled={!!downloading || !data}>
              {downloading === 'json' ? '⏳' : '⬇'} JSON
            </button>
          </div>
        </div>

        {/* API capability notice */}
        <div style={s.infoBar}>
          <span style={{ fontSize:16 }}>ℹ️</span>
          <div>
            <strong style={{ color:'#1e40af' }}>What the Cloud Billing API provides:</strong>
            <span style={{ color:'#3b82f6', marginLeft:8 }}>✓ Account metadata &nbsp;✓ Linked projects &nbsp;✓ Service catalog &nbsp;✓ SKU availability</span>
            <span style={{ color:'#94a3b8', marginLeft:16 }}>✗ Spend amounts (requires BigQuery Billing Export)</span>
          </div>
        </div>

        {error && <div style={s.errorBox}>⚠ {error}</div>}
        {loading && <div style={s.loadingWrap}><div style={s.spinner}/><span style={{color:'#94a3b8'}}>Fetching from GCP Cloud Billing API...</span></div>}

        {data && (
          <>
            {/* Summary cards */}
            <div style={s.statsGrid}>
              {[
                { icon:'🏦', label:'Account', value: data.billingAccount?.displayName || data.billingAccount?.id, small:true, color:'#2563eb' },
                { icon:'📂', label:'Total Projects', value: data.projects?.length || 0, color:'#16a34a' },
                { icon:'✅', label:'Billing Active', value: data.totalProjectsBilled || 0, color:'#16a34a' },
                { icon:'🔧', label:'Services in Catalog', value: data.totalServicesInCatalog?.toLocaleString() || '—', color:'#7c3aed' },
              ].map((c,i) => (
                <div key={i} style={s.statCard}>
                  <div style={{...s.statIcon, background:c.color+'15', border:`1px solid ${c.color}25`}}>{c.icon}</div>
                  <div style={{...s.statValue, color:c.color, fontSize: c.small ? 16 : 26}}>{c.value}</div>
                  <div style={s.statLabel}>{c.label}</div>
                </div>
              ))}
            </div>

            {/* Charts */}
            <div style={s.chartsRow}>
              <div style={s.chartCard}>
                <div style={s.chartHeader}>
                  <h3 style={s.chartTitle}>SKUs per Service</h3>
                  <span style={s.badge}>From API catalog</span>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={serviceBarData} margin={{left:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{fill:'#64748b',fontSize:10}} stroke="#e2e8f0" />
                    <YAxis tick={{fill:'#64748b',fontSize:11}} stroke="#e2e8f0" allowDecimals={false} />
                    <Tooltip contentStyle={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:8,boxShadow:'0 4px 12px rgba(0,0,0,0.08)'}} formatter={v=>[v,'SKUs available']}/>
                    <Bar dataKey="skus" radius={[6,6,0,0]}>
                      {serviceBarData.map((e,i) => <Cell key={i} fill={e.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div style={s.chartCard}>
                <div style={s.chartHeader}>
                  <h3 style={s.chartTitle}>Projects Billing Status</h3>
                  <span style={s.badge}>{data.projects?.length} total</span>
                </div>
                {projectPieData.length > 0 && (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={projectPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} stroke="#fff" strokeWidth={2}>
                        {projectPieData.map((e,i) => <Cell key={i} fill={e.color}/>)}
                      </Pie>
                      <Tooltip contentStyle={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:8}} formatter={(v,name)=>[`${v} project${v!==1?'s':''}`,name]}/>
                      <Legend formatter={v=><span style={{color:'#64748b',fontSize:12}}>{v}</span>}/>
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Services table */}
            <div style={s.tableCard}>
              <div style={s.tableHeader}>
                <h3 style={s.chartTitle}>Services on Billing Account</h3>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search services..." style={s.searchInput}/>
              </div>
              <table style={s.table}>
                <thead>
                  <tr style={{background:'#f8fafc'}}>
                    {['Service','Service ID','SKUs Available','Region Coverage'].map(h=><th key={h} style={s.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((svc,i) => (
                    <tr key={i} onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'} onMouseLeave={e=>e.currentTarget.style.background='#fff'} style={{background:'#fff'}}>
                      <td style={s.td}>
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <div style={{width:10,height:10,borderRadius:3,background:SERVICE_COLORS[svc.name]||'#94a3b8',flexShrink:0}}/>
                          <strong style={{color:'#0f172a'}}>{svc.name}</strong>
                        </div>
                      </td>
                      <td style={s.td}><code style={s.code}>{svc.id}</code></td>
                      <td style={s.td}>
                        <span style={{background:'#eff6ff',color:'#1d4ed8',padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:600}}>
                          {svc.skuCount}+ SKUs
                        </span>
                      </td>
                      <td style={s.td}>
                        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                          {svc.skus.slice(0,3).map((sk,j)=>
                            sk.regions?.slice(0,2).map((r,k)=>(
                              <span key={`${j}-${k}`} style={{background:'#f1f5f9',color:'#475569',padding:'1px 7px',borderRadius:10,fontSize:11}}>{r}</span>
                            ))
                          )}
                          {svc.skuCount > 3 && <span style={{color:'#94a3b8',fontSize:11}}>+more</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Projects table */}
            <div style={s.tableCard}>
              <div style={s.tableHeader}>
                <h3 style={s.chartTitle}>Linked Projects</h3>
                <span style={s.badge}>{data.projects?.length} projects</span>
              </div>
              <table style={s.table}>
                <thead>
                  <tr style={{background:'#f8fafc'}}>
                    {['Project ID','Billing Status','Billing Account'].map(h=><th key={h} style={s.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(data.projects||[]).map((p,i)=>(
                    <tr key={i} onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'} onMouseLeave={e=>e.currentTarget.style.background='#fff'} style={{background:'#fff'}}>
                      <td style={s.td}><code style={{...s.code,fontWeight:600,color:'#1d4ed8'}}>{p.projectId}</code></td>
                      <td style={s.td}>
                        <span style={{padding:'2px 10px',borderRadius:20,fontSize:11,fontWeight:600,background:p.billingEnabled?'#dcfce7':'#fee2e2',color:p.billingEnabled?'#166534':'#991b1b'}}>
                          {p.billingEnabled?'● Billing On':'○ Billing Off'}
                        </span>
                      </td>
                      <td style={s.td}><span style={{color:'#64748b',fontSize:12}}>{p.billingAccountName||data.billingAccount?.id}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* BigQuery note */}
            <div style={s.bigqueryNote}>
              <span style={{fontSize:20}}>📊</span>
              <div>
                <div style={{color:'#1e40af',fontWeight:700,fontSize:14,marginBottom:6}}>Want per-service cost amounts?</div>
                <div style={{color:'#3b82f6',fontSize:13,lineHeight:1.7}}>
                  Enable <strong>BigQuery Billing Export</strong> in{' '}
                  <a href="https://console.cloud.google.com/billing" target="_blank" rel="noreferrer" style={{color:'#2563eb'}}>
                    GCP Console → Billing → Billing Export
                  </a>
                  . Once enabled, this dashboard can query actual spend per service, SKU, project, and day.
                  Alternatively, download your cost CSV directly from{' '}
                  <a href="https://console.cloud.google.com/billing" target="_blank" rel="noreferrer" style={{color:'#2563eb'}}>
                    GCP Console → Billing → Reports → Download CSV
                  </a>.
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const s = {
  layout:{display:'flex',background:'#f5f7fa',minHeight:'100vh',fontFamily:"'Inter','Segoe UI',sans-serif"},
  main:{marginLeft:248,flex:1,padding:'28px 32px'},
  header:{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16},
  breadcrumb:{color:'#94a3b8',fontSize:12,fontWeight:500,marginBottom:4},
  title:{margin:0,color:'#0f172a',fontSize:24,fontWeight:700,letterSpacing:'-0.3px'},
  subtitle:{margin:'4px 0 0',color:'#94a3b8',fontSize:13},
  refreshTime:{color:'#94a3b8',fontSize:12},
  outlineBtn:{background:'#fff',border:'1.5px solid #e2e8f0',color:'#374151',borderRadius:8,padding:'8px 16px',cursor:'pointer',fontSize:13,fontWeight:500},
  infoBar:{display:'flex',alignItems:'flex-start',gap:12,background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:10,padding:'12px 16px',marginBottom:20,fontSize:13},
  errorBox:{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,padding:'12px 16px',color:'#dc2626',fontSize:14,marginBottom:20},
  loadingWrap:{display:'flex',alignItems:'center',gap:14,padding:60,justifyContent:'center'},
  spinner:{width:28,height:28,border:'3px solid #bfdbfe',borderTopColor:'#2563eb',borderRadius:'50%',animation:'spin 0.8s linear infinite'},
  statsGrid:{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20},
  statCard:{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'18px',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'},
  statIcon:{width:36,height:36,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,marginBottom:12},
  statValue:{fontSize:26,fontWeight:700,letterSpacing:'-0.3px',marginBottom:4},
  statLabel:{color:'#64748b',fontSize:12},
  chartsRow:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16},
  chartCard:{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'20px',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'},
  chartHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14},
  chartTitle:{margin:0,color:'#0f172a',fontSize:15,fontWeight:600},
  badge:{background:'#f1f5f9',color:'#64748b',fontSize:11,padding:'3px 10px',borderRadius:20},
  tableCard:{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,overflow:'hidden',marginBottom:16,boxShadow:'0 1px 4px rgba(0,0,0,0.04)'},
  tableHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',borderBottom:'1px solid #f1f5f9'},
  searchInput:{background:'#f8fafc',border:'1.5px solid #e2e8f0',borderRadius:8,padding:'7px 14px',fontSize:13,color:'#0f172a',outline:'none',width:200},
  table:{width:'100%',borderCollapse:'collapse'},
  th:{textAlign:'left',color:'#64748b',fontSize:11,fontWeight:600,letterSpacing:'0.5px',textTransform:'uppercase',padding:'10px 20px',borderBottom:'1px solid #f1f5f9'},
  td:{padding:'12px 20px',color:'#374151',fontSize:13,borderBottom:'1px solid #f8fafc',transition:'background 0.1s'},
  code:{background:'#f1f5f9',padding:'2px 8px',borderRadius:5,fontFamily:'monospace',fontSize:12,color:'#475569'},
  bigqueryNote:{display:'flex',gap:16,background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:12,padding:'18px 20px',alignItems:'flex-start'},
};